import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { reportsApi } from "@/lib/sprint2Entities";
import { apiError } from "@/lib/errors";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { BarChart3, Download, ChevronRight } from "lucide-react";
import { assetDisplay } from "@/lib/assetDisplay";

const REPORT_CATEGORIES = [
  { id: "operations",    label: "Operations" },
  { id: "fleet",         label: "Fleet" },
  { id: "drivers",       label: "Drivers" },
  { id: "relationships", label: "Customers & Relationships" },
  { id: "commercial",    label: "Commercial" },
];

// Report metadata scaffolding for the Reports Center.
// Fields:
//   k                    stable programmatic key
//   label                display label (product language — no phase / wave)
//   desc                 short subtitle
//   category             tab id (must match REPORT_CATEGORIES)
//   to                   route (present only when the report has a live implementation)
//   data_sources         collections + notable fields the query engine will read
//   unresolved_terms     legacy StarTracker terms whose business definition is
//                        pending owner clarification (rendered as an amber note)
//   implementation_status  "implemented" | "partial" | "not_yet_implemented"
const REPORT_CARDS = [
  // ---- Live reports (implemented / partial) ----
  {
    k: "active-tours", label: "Active Tours", category: "operations",
    desc: "Tours grouped by status.", to: "/reports/active-tours",
    data_sources: ["tours (status, start_date, end_date)"],
    unresolved_terms: [], implementation_status: "implemented",
  },
  {
    k: "upcoming-departures", label: "Upcoming Departures", category: "operations",
    desc: "Next 30 days.", to: "/reports/upcoming-departures",
    data_sources: ["tours (start_date)", "tour_asset_assignments"],
    unresolved_terms: [], implementation_status: "implemented",
  },
  {
    k: "coach-availability", label: "Coach Availability", category: "fleet",
    desc: "Grid of coaches × next 14 days.", to: "/reports/coach-availability",
    data_sources: ["assets", "tour_asset_assignments", "holds"],
    unresolved_terms: [], implementation_status: "implemented",
  },
  {
    k: "quote-pipeline", label: "Quote Pipeline", category: "commercial",
    desc: "Quotes by status with totals.", to: "/reports/quote-pipeline",
    data_sources: ["quote_versions (status, snapshot_pricing.totals)"],
    unresolved_terms: [], implementation_status: "implemented",
  },
  // Legacy StarTracker family: Bus/Trailer Utilization is subsumed by
  // the existing Fleet Utilization card. Metadata marks it partial.
  {
    k: "fleet-utilization", label: "Fleet Utilization", category: "fleet",
    desc: "Touring vs available vs maintenance days per coach. Covers legacy Bus/Trailer Utilization.",
    to: "/reports/fleet-utilization",
    data_sources: [
      "assets (asset_type)",
      "tour_asset_assignments (start_date, end_date)",
      "asset_status_history",
    ],
    unresolved_terms: [], implementation_status: "partial",
  },

  // ---- Legacy families — now implemented ----
  {
    k: "fuel-estimate", label: "Fuel Estimate", category: "commercial",
    desc: "Fuel-cost projection per tour.", to: "/reports/fuel-estimate",
    data_sources: ["tours (estimated_mileage)", "tour_pricing_line_items (fuel category)"],
    unresolved_terms: [], implementation_status: "implemented",
  },
  {
    k: "long-term-client", label: "Long Term Client", category: "relationships",
    desc: "Repeat customers and tenure.", to: "/reports/long-term-client",
    data_sources: ["customers", "tours (start_date, end_date)"],
    unresolved_terms: [], implementation_status: "implemented",
  },
  {
    k: "driver-availability", label: "Driver Availability", category: "drivers",
    desc: "Drivers by availability window.", to: "/reports/driver-availability",
    data_sources: [
      "drivers (availability_status, available_from, available_until, insurance_status)",
      "tour_driver_assignments",
    ],
    unresolved_terms: [], implementation_status: "implemented",
  },
  {
    k: "driver-tour-days", label: "Driver Tour Days", category: "drivers",
    desc: "Days each driver has been on tour.", to: "/reports/driver-tour-days",
    data_sources: [
      "tour_driver_assignments (start_date, end_date)",
      "tours (driver_deadhead_days)",
    ],
    unresolved_terms: [
      "DH Days = Deadhead Days (aggregate). DHF/DHR sub-components remain unresolved and are not expanded.",
    ],
    implementation_status: "implemented",
  },
  {
    k: "driver-utilization", label: "Driver Utilization", category: "drivers",
    desc: "% of days worked over a period.", to: "/reports/driver-utilization",
    data_sources: ["drivers", "tour_driver_assignments"],
    unresolved_terms: [], implementation_status: "implemented",
  },
  {
    k: "bus-availability", label: "Bus Availability", category: "fleet",
    desc: "Coaches by operational status window.", to: "/reports/bus-availability",
    data_sources: ["assets (operational_status)", "asset_status_history"],
    unresolved_terms: [], implementation_status: "implemented",
  },
  {
    k: "end-of-tour", label: "End of Tour", category: "operations",
    desc: "Tour close-out summary.", to: "/reports/end-of-tour",
    data_sources: ["tours (status, end_date, dropoff.datetime)", "tour_asset_assignments", "tour_pricing_line_items"],
    unresolved_terms: [],
    implementation_status: "implemented",
  },
  {
    k: "departures", label: "Departures", category: "operations",
    desc: "Departures schedule with pickup details.", to: "/reports/departures",
    data_sources: ["tours (start_date, pickup)", "departure_checklists"],
    unresolved_terms: [],
    implementation_status: "implemented",
  },
  {
    k: "client-contact", label: "Client Contact", category: "relationships",
    desc: "Client-facing contact roster.", to: "/reports/client-contact",
    data_sources: [
      "customers",
      "contact_organization_roles (role, is_primary)",
      "contacts",
    ],
    unresolved_terms: [], implementation_status: "implemented",
  },
];

export default function ReportsIndex() {
  const [active, setActive] = useState("operations");
  const visible = REPORT_CARDS.filter((r) => r.category === active);
  return (
    <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">Reports</SectionHeader>
      <PageHeader
        testid="page-reports"
        title={<span className="inline-flex items-center gap-2"><BarChart3 size={22} className="text-coach-blue" />Reports</span>}
        subtitle="Reports Center — filter by category, run a report, export as CSV. PDF and Print export are available on individual reports."
      />
      {/* Category tabs */}
      <div className="mb-4 border-b border-coach-silver flex flex-wrap gap-1" data-testid="reports-category-tabs">
        {REPORT_CATEGORIES.map((c) => {
          const count = REPORT_CARDS.filter((r) => r.category === c.id).length;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(c.id)}
              data-testid={`reports-tab-${c.id}`}
              data-active={active === c.id ? "true" : "false"}
              className={`text-[12.5px] px-3 py-2 -mb-px border-b-2 ${active === c.id ? "border-coach-blue text-carbon font-medium" : "border-transparent text-steel hover:text-carbon"}`}
            >
              {c.label} <span className="text-[10.5px] text-steel">({count})</span>
            </button>
          );
        })}
      </div>
      {visible.length === 0 ? (
        <div className="bg-white border border-coach-silver rounded-md p-8 text-center text-[13px] text-steel" data-testid="reports-empty-category">
          No reports are catalogued in this category yet. Choose another category, or open a specific record's Reports tab.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.map((r) => {
            const live = r.to && r.implementation_status !== "not_yet_implemented";
            const CardShell = ({ children }) =>
              live ? (
                <Link
                  to={r.to}
                  className="group bg-white border border-coach-silver rounded-md p-4 hover:border-coach-blue block"
                  data-testid={`report-card-${r.k}`}
                  data-status={r.implementation_status}
                >
                  {children}
                </Link>
              ) : (
                <div
                  className="bg-white border border-coach-silver rounded-md p-4 opacity-90"
                  data-testid={`report-card-${r.k}`}
                  data-status={r.implementation_status}
                >
                  {children}
                </div>
              );
            return (
              <CardShell key={r.k}>
                <div className="flex items-center justify-between">
                  <div className="font-display text-[15px] text-deep-road-navy">{r.label}</div>
                  {live ? (
                    <ChevronRight size={14} className="text-steel group-hover:text-coach-blue" />
                  ) : (
                    <span
                      className="text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-coach-silver text-steel"
                      data-testid={`report-status-${r.k}`}
                    >
                      Coming soon
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-steel mt-1">{r.desc}</div>
                {r.implementation_status === "partial" && (
                  <div
                    className="mt-2 text-[10.5px] uppercase tracking-widest text-amber-hold"
                    data-testid={`report-partial-${r.k}`}
                  >
                    Partial — subsumes a legacy family
                  </div>
                )}
                {r.unresolved_terms?.length > 0 && (
                  <div
                    className="mt-2 text-[10.5px] text-amber-hold"
                    data-testid={`report-unresolved-${r.k}`}
                  >
                    Unresolved legacy terms: {r.unresolved_terms.join(", ")}
                  </div>
                )}
              </CardShell>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}

// ---- Fleet Utilization report ----
export function ReportFleetUtilization() {
  const today = new Date();
  const [start, setStart] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10));
  const [end, setEnd] = useState(new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10));
  const [data, setData] = useState(null);

  const load = async () => {
    try { setData(await reportsApi.fleetUtil(start, end)); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  return (
    <PageShell>
      <PageHeader testid="page-report-fleet-util" title="Fleet Utilization"
        right={
          <a href={reportsApi.fleetUtilCsvUrl(start, end)} className="inline-flex items-center gap-1 text-coach-blue text-[12.5px] hover:underline"
            data-testid="fleet-util-csv"><Download size={12} /> CSV</a>
        } />
      <div className="flex items-center gap-2 mb-4 text-[13px]">
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
          className="border border-coach-silver rounded-sm px-2 py-1 text-[13px]" data-testid="fleet-util-start" />
        <span className="text-steel">→</span>
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
          className="border border-coach-silver rounded-sm px-2 py-1 text-[13px]" data-testid="fleet-util-end" />
      </div>
      <div className="bg-white border border-coach-silver rounded-md">
        <table className="w-full text-[13px]">
          <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
            <tr>
              <th className="text-left px-3 py-2">Coach</th>
              <th className="text-right px-3 py-2 w-24">Touring</th>
              <th className="text-right px-3 py-2 w-24">Maintenance</th>
              <th className="text-right px-3 py-2 w-24">Available</th>
              <th className="text-right px-3 py-2 w-24">Utilization</th>
              <th className="w-48 px-3">Bar</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.asset_id} className="border-t border-coach-silver/60">
                <td className="px-3 py-2 text-carbon">{assetDisplay(r)}</td>
                <td className="px-3 py-2 text-carbon text-right">{r.touring_days}</td>
                <td className="px-3 py-2 text-carbon text-right">{r.maintenance_days}</td>
                <td className="px-3 py-2 text-carbon text-right">{r.available_days}</td>
                <td className="px-3 py-2 text-coach-blue text-right font-medium">{r.utilization_percent}%</td>
                <td className="px-3 py-2">
                  <div className="h-2 bg-chrome-mist rounded-sm overflow-hidden">
                    <div className="h-full bg-coach-blue" style={{ width: `${r.utilization_percent}%` }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}

// ---- Active Tours ----
export function ReportActiveTours() {
  const [data, setData] = useState({});
  useEffect(() => { reportsApi.activeTours().then(setData).catch((e) => toast.error(apiError(e))); }, []);
  return (
    <PageShell>
      <PageHeader testid="page-report-active" title="Active Tours"
        right={<a href={reportsApi.activeToursCsvUrl()} className="inline-flex items-center gap-1 text-coach-blue text-[12.5px] hover:underline"
          data-testid="active-tours-csv"><Download size={12} /> CSV</a>} />
      <div className="space-y-3">
        {Object.entries(data).map(([status, items]) => (
          <div key={status} className="bg-white border border-coach-silver rounded-md">
            <div className="px-4 py-2 text-[11px] uppercase tracking-widest text-steel border-b border-coach-silver/60">
              {status.replaceAll("_", " ")} · {items.length}
            </div>
            <ul>
              {items.map((t) => (
                <li key={t.id} className="px-4 py-2 border-b border-coach-silver/60 last:border-0 flex justify-between text-[13px]">
                  <Link to={`/tours/${t.id}`} className="text-coach-blue hover:underline">{t.tour_number} · {t.display_name}</Link>
                  <span className="text-steel">{t.start_date} → {t.end_date}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

// ---- Upcoming Departures ----
export function ReportUpcoming() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ rows: [] });
  useEffect(() => { reportsApi.upcoming(days).then(setData).catch((e) => toast.error(apiError(e))); }, [days]);
  return (
    <PageShell>
      <PageHeader testid="page-report-upcoming" title="Upcoming Departures"
        right={<a href={reportsApi.upcomingCsvUrl(days)} className="inline-flex items-center gap-1 text-coach-blue text-[12.5px] hover:underline"
          data-testid="upcoming-csv"><Download size={12} /> CSV</a>} />
      <div className="flex items-center gap-2 mb-3 text-[13px]">
        <span className="text-steel">Range:</span>
        {[7, 14, 30, 60].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-2 py-1 rounded-sm text-[12.5px] ${days === d ? "bg-coach-blue text-white" : "border border-coach-silver text-carbon"}`}
            data-testid={`upcoming-range-${d}`}>{d}d</button>
        ))}
      </div>
      <div className="bg-white border border-coach-silver rounded-md">
        <table className="w-full text-[13px]">
          <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
            <tr>
              <th className="text-left px-3 py-2">Tour</th>
              <th className="text-left px-3 py-2 w-32">Status</th>
              <th className="text-left px-3 py-2 w-44">Dates</th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).map((r) => (
              <tr key={r.id} className="border-t border-coach-silver/60">
                <td className="px-3 py-2"><Link to={`/tours/${r.id}`} className="text-coach-blue hover:underline">{r.tour_number} · {r.display_name}</Link></td>
                <td className="px-3 py-2 text-steel">{r.status.replaceAll("_", " ")}</td>
                <td className="px-3 py-2 text-steel">{r.start_date} → {r.end_date}</td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && (
              <tr><td colSpan={3} className="px-3 py-6 text-center text-steel">No upcoming departures.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}

// ---- Coach Availability grid ----
export function ReportCoachAvailability() {
  const [data, setData] = useState(null);
  useEffect(() => { reportsApi.coachAvail(14).then(setData).catch((e) => toast.error(apiError(e))); }, []);
  const cellColor = (s) =>
    s === "touring" ? "bg-coach-blue text-white"
      : s === "maintenance" ? "bg-maintenance-orange text-white"
        : "bg-touring-lime/60 text-carbon";
  return (
    <PageShell>
      <PageHeader testid="page-report-avail" title="Coach Availability (14 days)" />
      <div className="bg-white border border-coach-silver rounded-md overflow-x-auto">
        <table className="text-[11.5px]">
          <thead className="bg-road-white text-[10px] uppercase tracking-widest text-steel">
            <tr>
              <th className="text-left px-2 py-2 sticky left-0 bg-road-white">Coach</th>
              {(data?.dates || []).map((d) => <th key={d} className="text-center px-1.5 py-2 w-16">{d.slice(5)}</th>)}
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.fleet_number || r.asset_id || r.unit_name} className="border-t border-coach-silver/60">
                <td className="px-2 py-1.5 sticky left-0 bg-white text-carbon">{assetDisplay(r)}</td>
                {(r.cells || []).map((c, i) => (
                  <td key={i} className="px-1 py-1"><div className={`text-center rounded-sm py-1 ${cellColor(c.status)}`}>{c.status[0].toUpperCase()}</div></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3 mt-2 text-[11px] text-steel">
        <span className="inline-block w-3 h-3 bg-touring-lime/60 mr-1"></span>Available
        <span className="inline-block w-3 h-3 bg-coach-blue mr-1"></span>Touring
        <span className="inline-block w-3 h-3 bg-maintenance-orange mr-1"></span>Maintenance
      </div>
    </PageShell>
  );
}

// ---- Quote Pipeline ----
export function ReportQuotePipeline() {
  const [data, setData] = useState({});
  useEffect(() => { reportsApi.quotePipeline().then(setData).catch((e) => toast.error(apiError(e))); }, []);
  const fmt = (v) => Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });
  return (
    <PageShell>
      <PageHeader testid="page-report-pipeline" title="Quote Pipeline"
        right={<a href={reportsApi.quotePipelineCsvUrl()} className="inline-flex items-center gap-1 text-coach-blue text-[12.5px] hover:underline"
          data-testid="pipeline-csv"><Download size={12} /> CSV</a>} />
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {["draft", "sent", "accepted", "declined", "superseded"].map((s) => (
          <div key={s} className="bg-white border border-coach-silver rounded-md p-4">
            <div className="text-[10.5px] uppercase tracking-widest text-steel">{s}</div>
            <div className="font-display text-3xl text-deep-road-navy leading-tight">{data[s]?.count || 0}</div>
            <div className="text-[11.5px] text-steel mt-1">{fmt(data[s]?.total || "0")}</div>
          </div>
        ))}
      </div>
    </PageShell>
  );
}
